import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import RedeemIcon from "@mui/icons-material/Redeem";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDashedDateWithYear } from "../../../utils/hooks/dataHandler";
import RemoveDialog from "../../utils/dialog/RemoveDialog";
import BaseDialog from "../../utils/dialog/BaseDialog";

export const statusColorMap = {
  active: "success",
  inactive: "error",
  pending: "warning",
  banned: "primary",
};

const UserProfileDialog = ({
  open,
  onClose,
  user,
  onRoleChange,
  onNameChange,
  onUpdateSubsidiaryUser,
  onTerminateSubsidiaryUser,
  onDeleteUser,
  onUserUpdated,
  onRefreshUsers,
  isTaxProfessionalPlan = false,
  showSuccess,
  showError,
}) => {
  const theme = useTheme();

  const [newRole, setNewRole] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [updatingName, setUpdatingName] = useState(false);
  const [syncCompanyRole, setSyncCompanyRole] = useState(false);

  const isSubsidiary = user && user.user_auth?.user_type === "subsidiary";
  const isBanned = isSubsidiary && user.user_auth.status === "banned";
  const expiryDate = user && user.user_auth?.expire_date;
  const currentRole = user?.user_role || user?.user_auth?.user_role || "";
  const effectiveRole =
    newRole || currentRole;

  const roleDescriptions = {
    admin: isTaxProfessionalPlan
      ? "Account-level admin. Can manage team members and account settings. Company-level roles and year restrictions are managed separately in Permission Manager."
      : "Highest account access. Can manage team members, companies, CA settings, and tax computations available to the account.",
    editor: isTaxProfessionalPlan
      ? "Account-level editor. This role controls general system authorization only. Their role for each company can be assigned separately in Permission Manager."
      : "Can work on tax computations, but cannot manage team members or restricted account settings such as CA settings.",
    viewer: isTaxProfessionalPlan
      ? "Account-level viewer. This role controls general read-only authorization only. Company access, company role, and year restrictions can be refined in Permission Manager."
      : "Read-only account access. Can view tax computations but cannot make changes.",
  };

  useEffect(() => {
    setSyncCompanyRole(false);
  }, [open, user?.u_id]);

  const handleEditName = async () => {
    if (!editedName.trim() || editedName === user.name) {
      setIsEditingName(false);
      return;
    }

    setUpdatingName(true);
    try {
      if (!isSubsidiary)
        await onNameChange({ sub_uid: user.u_id, newName: editedName });
      else
        await onUpdateSubsidiaryUser({
          targetId: user.u_id,
          updates: {
            name: editedName,
            phone_number: user.phone_number,
            address: user.address,
            user_role: user.user_auth.user_role,
            status: user.user_auth.status,
          },
        });
      showSuccess("Name updated successfully");
      user.name = editedName;
      setIsEditingName(false);
    } catch (err) {
      showError(err);
    } finally {
      setUpdatingName(false);
    }
  };

  const startEditing = () => {
    setEditedName(user.name);
    setIsEditingName(true);
  };

  const cancelEditing = () => {
    setEditedName("");
    setIsEditingName(false);
  };

  const handleSaveRole = async () => {
    setUpdating(true);
    try {
      if (!isSubsidiary)
        await onRoleChange({
          sub_uid: user.u_id,
          newRole,
          ...(isTaxProfessionalPlan && {
            syncUserCompanyRole: syncCompanyRole,
          }),
        });
      else
        await onUpdateSubsidiaryUser({
          targetId: user.u_id,
          ...(isTaxProfessionalPlan && {
            syncUserCompanyRole: syncCompanyRole,
          }),
          updates: {
            name: user.name,
            phone_number: user.phone_number,
            address: user.address,
            user_role: newRole,
            status: user.user_auth.status,
          },
        });
      showSuccess("Role updated successfully");
      const updatedUser = isSubsidiary
        ? {
            ...user,
            user_auth: {
              ...(user.user_auth || {}),
              user_role: newRole,
            },
          }
        : {
            ...user,
            user_role: newRole,
          };
      onUserUpdated?.(updatedUser);
      onRefreshUsers?.();
      setNewRole("");
      setSyncCompanyRole(false);
    } catch (err) {
      showError(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    setIsDeleting(true);
    try {
      if (isSubsidiary) await onTerminateSubsidiaryUser(user.u_id);
      else await onDeleteUser({ sub_uid: user.u_id });
      showSuccess("Your request has been sucessfully executed.");
      onClose(true);
    } catch (err) {
      showError(err);
    } finally {
      setDeleting(false);
      setIsDeleting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title='User Profile'
      subtitle='Manage your team member details'
      icon={<PermContactCalendarIcon />}
      actions={
        <>
          {!isBanned && (
            <Button
              variant='contained'
              onClick={() => setDeleting(true)}
              sx={{
                backgroundColor: "error.main",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
              }}
            >
              {isSubsidiary ? "Terminate User" : "Delete User"}
            </Button>
          )}

          <Box flexGrow={1} />

          <Button onClick={onClose}>Cancel</Button>

          <Button
            onClick={handleSaveRole}
            variant='contained'
            disabled={updating || !newRole || newRole === currentRole}
          >
            {updating ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </>
      }
    >
      {user && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Box display='flex' alignItems='center' gap={2} flexWrap='wrap'>
              <Box flexGrow={1} minWidth={200}>
                {isEditingName ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <TextField
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      size='small'
                      fullWidth
                      autoFocus
                      disabled={updatingName}
                      variant='standard'
                      sx={{
                        "& .MuiInput-input": {
                          fontSize: "1.25rem",
                          fontWeight: 600,
                        },
                      }}
                    />
                    <Box display='flex' gap={0.5}>
                      <IconButton
                        onClick={handleEditName}
                        disabled={
                          updatingName ||
                          !editedName.trim() ||
                          editedName === user.name
                        }
                        size='small'
                        color='primary'
                      >
                        {updatingName ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CheckIcon fontSize='small' />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={cancelEditing}
                        disabled={updatingName}
                        size='small'
                        color='error'
                      >
                        <CancelIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography
                      variant='h5'
                      fontWeight={700}
                      color='text.primary'
                      sx={{ letterSpacing: "-0.02em" }}
                    >
                      {user.name}
                    </Typography>
                    {!isBanned && (
                      <IconButton
                        onClick={startEditing}
                        size='small'
                        sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>

              <Box display='flex' gap={1} alignItems='center'>
                <Chip
                  label={user.user_role || user.user_auth?.user_role}
                  size='small'
                  color='primary'
                  variant='filled'
                  sx={{ fontWeight: 600, textTransform: "capitalize" }}
                />
                <Chip
                  label={user.status || user.user_auth?.status}
                  size='small'
                  color={
                    statusColorMap[user.status || user.user_auth?.status] ||
                    "default"
                  }
                  variant='outlined'
                  sx={{ textTransform: "capitalize", borderWidth: 1 }}
                />
              </Box>
            </Box>

            {isSubsidiary && (
              <Box
                display='flex'
                alignItems='center'
                gap={1.5}
                sx={{ mt: 1.5, pl: 0.5 }}
              >
                <Chip
                  label='Subsidiary'
                  size='small'
                  icon={<RedeemIcon fontSize='small' />}
                  sx={{
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                    color: "text.secondary",
                    fontWeight: 500,
                    height: 24,
                  }}
                />

                {expiryDate && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <AccessTimeIcon fontSize='inherit' />
                    Expires: {formatDashedDateWithYear(expiryDate)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1.5}
                    p={1.5}
                    borderRadius={1}
                    sx={{ backgroundColor: "action.hover" }} // 添加轻微背景色
                  >
                    <EmailIcon color='action' sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        Email Address
                      </Typography>
                      <Typography variant='body2' fontWeight={500}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1.5}
                    p={1.5}
                    borderRadius={1}
                    sx={{ backgroundColor: "action.hover" }}
                  >
                    <PhoneIcon color='action' sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        Phone Number
                      </Typography>
                      <Typography variant='body2' fontWeight={500}>
                        {user.phone_number || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size='small'>
                <InputLabel id='user-role-label'>Account Role</InputLabel>
                <Select
                  labelId='user-role-label'
                  label='Account Role'
                  value={
                    newRole || user.user_role || user.user_auth?.user_role || ""
                  }
                  onChange={(e) => setNewRole(e.target.value)}
                  disabled={isBanned}
                  variant='outlined'
                  sx={{
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  <MenuItem value='admin'>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <AdminPanelSettingsIcon
                        sx={{ mr: 1, color: "primary.main" }}
                        fontSize='small'
                      />
                      Admin
                    </Box>
                  </MenuItem>
                  <MenuItem value='editor'>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <EditIcon
                        sx={{ mr: 1, color: "text.secondary" }}
                        fontSize='small'
                      />
                      Editor
                    </Box>
                  </MenuItem>
                  <MenuItem value='viewer'>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <VisibilityIcon
                        sx={{ mr: 1, color: "text.secondary" }}
                        fontSize='small'
                      />
                      Viewer
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: isTaxProfessionalPlan
                    ? alpha(theme.palette.info.main, 0.06)
                    : "action.hover",
                  border: "1px solid",
                  borderColor: isTaxProfessionalPlan
                    ? alpha(theme.palette.info.main, 0.18)
                    : "divider",
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ display: "block", fontWeight: 600 }}
                >
                  {roleDescriptions[effectiveRole] ?? "Select an account role"}
                </Typography>

                {isTaxProfessionalPlan && (
                  <>
                    <FormControlLabel
                      sx={{ mt: 0.75 }}
                      control={
                        <Checkbox
                          size='small'
                          checked={syncCompanyRole}
                          onChange={(e) => setSyncCompanyRole(e.target.checked)}
                          disabled={isBanned || updating}
                        />
                      }
                      label={
                        <Typography variant='caption' color='text.secondary'>
                          Match company roles to this account role
                        </Typography>
                      }
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      <RemoveDialog
        icon={PersonRemoveIcon}
        title={`${isSubsidiary ? "Terminate" : "Remove"} User`}
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={handleDeleteUser}
        loading={isDeleting}
      >
        <Typography
          color='text.secondary'
          sx={{ fontSize: 16, mb: 1, textAlign: "justify" }}
        >
          You're about to {isSubsidiary ? "terminate" : "remove"} the user. Are
          you sure you want to proceed?
        </Typography>
      </RemoveDialog>
    </BaseDialog>
  );
};

export default UserProfileDialog;
